import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Row, Col } from "reactstrap";
import { fetchCourseListForWebThunk } from "slices/CourseWeb/thunk";
import { fetchProductListForWebThunk } from "slices/ProductWeb/thunk";
import type { ThunkDispatch } from "redux-thunk";
import type { AnyAction } from "redux";
import type { RootState } from "store";
import type { Product } from "services/productService";
import DashboardHeader from "../../Components/Common/DashboardHeader";

const selectCourseWebState = (state: RootState) => (state as any).courseWeb;
const selectProductWebState = (state: RootState) => (state as any).productWeb;

const courseDataSelector = createSelector(selectCourseWebState, (courseWeb) => ({
  courseData: courseWeb.courses,
  loading: courseWeb.loading,
  pagination: courseWeb.pagination,
}));

const productDataSelector = createSelector(selectProductWebState, (productWeb) => ({
  products: productWeb.products,
  loading: productWeb.loading,
  error: productWeb.error,
}));

const ExploreCoursesAndInsurance = () => {
  const dispatch: ThunkDispatch<RootState, any, AnyAction> = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { courseData, loading: coursesLoading, pagination } = useSelector(courseDataSelector);
  const { products, loading: insuranceLoading, error } = useSelector(productDataSelector);
  
  const coursesSectionRef = useRef<HTMLElement>(null);
  const insuranceSectionRef = useRef<HTMLElement>(null);
  const didRequestCoursesRef = useRef(false);
  const didRequestProductsRef = useRef(false);

  useEffect(() => {
    if (didRequestCoursesRef.current) return;
    if (!coursesLoading && (!courseData || courseData.length === 0)) {
      didRequestCoursesRef.current = true;
      dispatch(
        fetchCourseListForWebThunk({
          page: 1,
          per_page: 1000, 
          from_date: "",
          to_date: "",
          course_id: 0,
          searchkey: "",
        })
      );
    }
  }, [dispatch, coursesLoading, courseData]);

  useEffect(() => {
    if (didRequestProductsRef.current) return;
    if (!insuranceLoading && (!products || products.length === 0)) {
      didRequestProductsRef.current = true;
      dispatch(
        fetchProductListForWebThunk({
          page: 1,
          per_page: 1000,
          product_code: "",
          product_category: "",
          product_name: "",
        })
      );
    }
  }, [dispatch, insuranceLoading, products]);

  useEffect(() => {
    const path = location.pathname;
    const hash = location.hash;
    
    setTimeout(() => {
      if (hash === "#insurance" || path === "/insurance") {
        insuranceSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (hash === "#courses" || path === "/services" || path === "/explore-courses") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }, 100);
  }, [location.pathname, location.hash]);


  const handleInterested = (productId: number) => {
    navigate(`/insurance/${productId}`);
  };

  const handleCardClick = (productId: number, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.insurance-interested-btn')) {
      return;
    }
    navigate(`/insurance/${productId}`);
  };

  const formatNumberWithCommas = (num: number | string): string => {
    const numStr = typeof num === "string" ? num.replace(/,/g, "").replace(/\./g, "") : num.toString();
    const numValue = parseInt(numStr, 10);
    if (isNaN(numValue)) return numStr;
    return numValue.toLocaleString("en-US");
  };

  const getCoverageAmount = (product: Product): string => {
    const maxSumAssured = product.product_additional_info?.maximum_sum_assured;
    if (maxSumAssured && maxSumAssured !== "No Upper Limit") {
      const match = maxSumAssured.match(/(\d+(?:[.,]\d+)*)/);
      if (match) {
        const numberStr = match[1].replace(/\./g, "").replace(/,/g, "");
        return formatNumberWithCommas(numberStr);
      }
    }
    const minSumAssured = product.product_additional_info?.minimum_sum_assured;
    if (minSumAssured) {
      const match = minSumAssured.match(/(\d+(?:[.,]\d+)*)/);
      if (match) {
        const numberStr = match[1].replace(/\./g, "").replace(/,/g, "");
        return formatNumberWithCommas(numberStr);
      }
    }
    return "500";
  };

  const formatPolicyTerm = (policyTerm: string | undefined): string => {
    if (!policyTerm) return "Policy term varies";
    
    if (policyTerm.toLowerCase().includes("whole life")) {
      return "Policy term Whole Life (up to age 80) of your age";
    }
    
    const rangeMatch = policyTerm.match(/(\d+)\s*[-/]\s*(\d+)/);
    if (rangeMatch) {
      return `Policy term ${rangeMatch[1]} - ${rangeMatch[2]} Years of your age`;
    }
    
    const multipleMatch = policyTerm.match(/(\d+(?:\/\d+)+)/);
    if (multipleMatch) {
      return `Policy term ${multipleMatch[1]} years of your age`;
    }
    
    const singleMatch = policyTerm.match(/(\d+)/);
    if (singleMatch) {
      return `Policy term ${singleMatch[1]} Years of your age`;
    }
    
    return `Policy term ${policyTerm} of your age`;
  };

  const getKeyFeatures = (product: Product): string[] => {
    const features: string[] = [];
    const additional = product.product_additional_info;

    if (additional?.policy_term) {
      features.push(formatPolicyTerm(additional.policy_term));
    }

    const guaranteedBenefit = product.product_benfit?.find((b) =>
      b.benfit_title.toLowerCase().includes("guaranteed") ||
      b.benfit_title.toLowerCase().includes("return") ||
      b.benfit_title.toLowerCase().includes("cover") ||
      b.benfit_title.toLowerCase().includes("protection") ||
      b.benfit_title.toLowerCase().includes("life cover")
    );
    if (guaranteedBenefit) {
      const title = guaranteedBenefit.benfit_title.replace(/:/g, "").trim();
      if (title.toLowerCase().includes("guaranteed")) {
        features.push("Gives you Guaranteed Returns");
      } else if (title.toLowerCase().includes("life cover") || title.toLowerCase().includes("comprehensive life cover")) {
        features.push("Gives you Comprehensive Life Cover");
      } else if (title.toLowerCase().includes("lifetime protection")) {
        features.push("Gives you Lifetime Protection");
      } else if (title.toLowerCase().includes("cover") || title.toLowerCase().includes("protection")) {
        features.push(`Gives you ${title}`);
      } else {
        features.push(`Gives you ${title}`);
      }
    } else if (product.product_benfit && product.product_benfit.length > 0) {
      const title = product.product_benfit[0].benfit_title.replace(/:/g, "").trim();
      features.push(`Gives you ${title}`);
    }

    const maxSumAssured = additional?.maximum_sum_assured;
    if (maxSumAssured && maxSumAssured !== "No Upper Limit" && maxSumAssured.toLowerCase() !== "no upper limit") {
      const match = maxSumAssured.match(/(\d+(?:[.,]\d+)*)/);
      if (match) {
        const numberStr = match[1].replace(/\./g, "").replace(/,/g, "");
        const formattedMax = formatNumberWithCommas(numberStr);
        features.push(`Max Sum Assured up to BD ${formattedMax}`);
      } else {
        features.push(`Max Sum Assured up to BD ${maxSumAssured}`);
      }
    } else {
      features.push("No upper limit for Max Sum Assured");
    }

    return features.slice(0, 3);
  };

  document.title = "Explore Courses & Insurance - Amanat";

  return (
    <div className="explore-courses-insurance-page">
      <DashboardHeader />

      <main className="explore-main">
        <div className="container">
          <section ref={coursesSectionRef} id="courses" className="courses-section-wrapper">
            <div className="intro-section">
              <h1 className="main-heading courses-main-heading">Explore Courses</h1>
              <p className="intro-text courses-intro-text">
                Amanat helps you learn the latest, in-demand skills quickly and easily, from anywhere. Whether you're starting your career, switching fields, or simply leveling up, Amanat gives you the right courses to stay ahead in today's fast-changing job market. With expert instructors, practical lessons, and learning at your own pace, Amanat makes growing in your career simpler than ever.
              </p>
            </div>

            <section className="courses-section">
              {coursesLoading ? (
                <div className="loading-state">Loading courses...</div>
              ) : courseData && courseData.length > 0 ? (
                <div className="course-cards-container">
                  <Row className="g-3 course-cards-row">
                    {courseData.map((course: any) => (
                      <Col lg={4} md={6} sm={12} key={course.id} className="mb-3">
                        <Link 
                          to={`/services/${course.id}`}
                          className="course-card-link"
                        >
                          <div className="course-card">
                            <div className="course-image-wrapper">
                              <img 
                                src={course.thumbnail_url || "https://via.placeholder.com/400x250"} 
                                alt={course.course_name}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = "https://via.placeholder.com/400x250";
                                }}
                              />
                            </div>
                            <div className="course-content">
                              <h2 className="course-title">{course.course_name || "Course Title"}</h2>
                              <p className="course-description">
                                {course.description || "Learn fundamental and advanced data analysis skills, covering Python, SQL, statistics, data visualization, and business intelligence tools."}
                              </p>
                            </div>
                            <button className="view-now-btn" type="button">
                              View Now <i className="ri-arrow-right-line"></i>
                            </button>
                          </div>
                        </Link>
                      </Col>
                    ))}
                  </Row>
                </div>
              ) : (
                <div className="no-courses">No courses available at the moment.</div>
              )}
            </section>
          </section>

          <section ref={insuranceSectionRef} id="insurance" className="insurance-section-wrapper">
            <div className="intro-section">
              <h1 className="main-heading insurance-main-heading">Explore Insurance products</h1>
              <p className="intro-text insurance-intro-text">
                Amanat Insurance offers dependable protection with thoughtfully designed plans that give you peace of mind at every stage of life. This course is created to help you understand insurance concepts clearly, explore policy benefits, and make confident decisions without confusion. With simple explanations and practical insights, Amanat Insurance ensures you're not just covered—but informed, prepared, and secure for the future.
              </p>
            </div>

            <section className="products-section">
              <div className="insurance-cards-container">
                {insuranceLoading ? (
                  <div className="text-center py-5">
                    <p>Loading insurance products...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5">
                    <p className="text-danger">Error loading products: {error}</p>
                  </div>
                ) : products.length === 0 ? (
                  <div className="text-center py-5">
                    <p>No insurance products available.</p>
                  </div>
                ) : (
                  <Row className="g-3 insurance-cards-row">
                    {products.map((product: Product) => {
                      const coverageAmount = getCoverageAmount(product);
                      const keyFeatures = getKeyFeatures(product);

                      return (
                        <Col lg={4} md={6} sm={12} key={product.id} className="mb-3">
                          <div 
                            className="insurance-card h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => handleCardClick(product.id, e)}
                          >
                            <div className="insurance-card-image">
                              <img
                                src={product.product_image_url || "/placeholder-insurance.jpg"}
                                alt={product.product_name}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).src = "/placeholder-insurance.jpg";
                                }}
                              />
                            </div>

                            <div className="insurance-card-body">
                              <h3 className="insurance-product-name">{product.product_name}</h3>
                              <span className="insurance-plan-number">
                                Plan {product.product_code}
                              </span>

                              <div className="insurance-coverage-amount">
                                <strong className="coverage-amount-value">BD {coverageAmount}</strong>
                                <span className="coverage-label">Coverage</span>
                              </div>

                              {keyFeatures.length > 0 && (
                                <ul className="insurance-features-list">
                                  {keyFeatures.map((feature, index) => (
                                    <li key={index} className="insurance-feature-item">
                                      <span>{feature}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}

                              <button
                                className="insurance-interested-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInterested(product.id);
                                }}
                              >
                                I'm interested
                              </button>
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                )}
              </div>
            </section>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ExploreCoursesAndInsurance;

